import { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { Calendar, MapPin, PoundSterling, User, Mail, Phone, Plus, Building } from "lucide-react";
import { VenueFormModal } from "@/components/venues/venue-form-modal";
import type { ExternalEvent, Venue } from "@shared/schema";

interface ExternalEventFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organisationId: number;
  event?: ExternalEvent & { venue?: Venue };
}

export function ExternalEventFormModal({ open, onOpenChange, organisationId, event }: ExternalEventFormModalProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [showVenueModal, setShowVenueModal] = useState(false);
  const [selectedVenue, setSelectedVenue] = useState<Venue | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    venueId: 0,
    date: "",
    budget: "",
    useVenueContact: true,
    contactName: "",
    contactEmail: "",
    contactPhone: "",
    notes: "",
    status: "tentative" as "tentative" | "confirmed" | "cancelled" | "completed",
  });

  // Fetch venues
  const { data: venues = [] } = useQuery<Venue[]>({
    queryKey: [`/api/venues?organisationId=${organisationId}`],
    queryFn: async () => {
      const response = await fetch(`/api/venues?organisationId=${organisationId}&active=true`);
      if (!response.ok) throw new Error("Failed to fetch venues");
      return response.json();
    },
    enabled: open,
  });

  // Load existing event data if editing
  useEffect(() => {
    if (event) {
      setFormData({
        name: event.name,
        venueId: event.venueId || 0,
        date: new Date(event.date).toISOString().slice(0, 16), // Format for datetime-local input
        budget: event.budget ? (event.budget / 100).toString() : "",
        useVenueContact: event.useVenueContact ?? true,
        contactName: event.contactName || "",
        contactEmail: event.contactEmail || "",
        contactPhone: event.contactPhone || "",
        notes: event.notes || "",
        status: event.status as "tentative" | "confirmed" | "cancelled" | "completed",
      });
      
      // Set selected venue if event has one
      if (event.venue) {
        setSelectedVenue(event.venue);
      }
    } else {
      // Reset form for new event
      setFormData({
        name: "",
        venueId: 0,
        date: "",
        budget: "",
        useVenueContact: true,
        contactName: "",
        contactEmail: "",
        contactPhone: "",
        notes: "",
        status: "tentative",
      });
      setSelectedVenue(null);
    }
  }, [event]); // Only depend on event prop, not venues

  // Update selected venue when formData.venueId changes (but not on initial load)
  useEffect(() => {
    // Skip if we're editing an existing event (already handled above)
    if (!event && formData.venueId && venues.length > 0) {
      const venue = venues.find(v => v.id === formData.venueId);
      if (venue) {
        setSelectedVenue(venue);
      }
    }
  }, [formData.venueId, venues, event]);

  // Create/update event mutation
  const mutation = useMutation({
    mutationFn: async (data: any) => {
      const url = event 
        ? `/api/external-events/${event.id}`
        : `/api/external-events?organisationId=${organisationId}`;
      
      const response = await fetch(url, {
        method: event ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to save event");
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/external-events`] });
      toast({
        title: event ? "Event Updated" : "Event Created",
        description: event ? "Event details have been updated." : "New event has been created.",
      });
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleSubmit = () => {
    if (!formData.name || !formData.venueId || !formData.date) {
      toast({
        title: "Missing Required Fields",
        description: "Please fill in event name, venue, and date.",
        variant: "destructive",
      });
      return;
    }

    const data: any = {
      name: formData.name,
      venueId: formData.venueId,
      date: new Date(formData.date).toISOString(),
      budget: formData.budget ? Math.round(parseFloat(formData.budget) * 100) : undefined,
      useVenueContact: formData.useVenueContact,
      contactName: formData.useVenueContact ? undefined : (formData.contactName || undefined),
      contactEmail: formData.useVenueContact ? undefined : (formData.contactEmail || undefined),
      contactPhone: formData.useVenueContact ? undefined : (formData.contactPhone || undefined),
      notes: formData.notes || undefined,
      status: formData.status,
    };

    mutation.mutate(data);
  };

  const handleVenueCreated = (venue: Venue) => {
    // Refresh venues list
    queryClient.invalidateQueries({ queryKey: [`/api/venues`] });
    // Select the newly created venue
    setFormData(prev => ({ ...prev, venueId: venue.id }));
    setSelectedVenue(venue);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="w-[95vw] max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{event ? "Edit Event" : "Create External Event"}</DialogTitle>
            <DialogDescription>
              {event ? "Update the event details." : "Add a new external event for booking acts."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Event Details */}
            <div>
              <Label htmlFor="name">
                Event Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Summer Music Festival"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="venue">
                  <Building className="inline h-4 w-4 mr-1" />
                  Venue <span className="text-red-500">*</span>
                </Label>
                <div className="flex gap-2">
                  <Select
                    value={formData.venueId.toString()}
                    onValueChange={(value) => {
                      const venueId = parseInt(value);
                      const venue = venues.find(v => v.id === venueId);
                      setFormData(prev => ({
                        ...prev,
                        venueId,
                        // If using venue contact, update contact fields when venue changes
                        ...(prev.useVenueContact && venue ? {
                          contactName: venue.contactName || "",
                          contactEmail: venue.contactEmail || "",
                          contactPhone: venue.contactPhone || "",
                        } : {})
                      }));
                    }}
                  >
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder="Select a venue" />
                    </SelectTrigger>
                    <SelectContent>
                      {venues.map((venue) => (
                        <SelectItem key={venue.id} value={venue.id.toString()}>
                          {venue.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    type="button"
                    size="icon"
                    variant="outline"
                    onClick={() => setShowVenueModal(true)}
                    title="Add new venue"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {selectedVenue && (
                  <p className="text-xs text-slate-600 mt-1">
                    <MapPin className="inline h-3 w-3 mr-1" />
                    {selectedVenue.address}
                    {selectedVenue.city && `, ${selectedVenue.city}`}
                    {selectedVenue.state && `, ${selectedVenue.state}`}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="date">
                  <Calendar className="inline h-4 w-4 mr-1" />
                  Date & Time <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="date"
                  type="datetime-local"
                  value={formData.date}
                  onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="budget">
                  <PoundSterling className="inline h-4 w-4 mr-1" />
                  Budget
                </Label>
                <Input
                  id="budget"
                  type="number"
                  step="0.01"
                  value={formData.budget}
                  onChange={(e) => setFormData(prev => ({ ...prev, budget: e.target.value }))}
                  placeholder="5000.00"
                />
              </div>
              <div>
                <Label htmlFor="status">Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value: any) => setFormData(prev => ({ ...prev, status: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tentative">Tentative</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Contact Information */}
            <div className="space-y-4 pt-4 border-t">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="useVenueContact"
                    checked={formData.useVenueContact}
                    onCheckedChange={(checked) => {
                      const useVenue = checked as boolean;
                      setFormData(prev => ({
                        ...prev,
                        useVenueContact: useVenue,
                        // If switching to use venue contact, populate with venue's contact info
                        ...(useVenue && selectedVenue ? {
                          contactName: selectedVenue.contactName || "",
                          contactEmail: selectedVenue.contactEmail || "",
                          contactPhone: selectedVenue.contactPhone || "",
                        } : {})
                      }));
                    }}
                  />
                  <Label htmlFor="useVenueContact" className="text-sm cursor-pointer">
                    Use venue contact as event contact
                  </Label>
                </div>
              </div>

              {!formData.useVenueContact && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="contactName">
                      <User className="inline h-4 w-4 mr-1" />
                      Contact Name
                    </Label>
                    <Input
                      id="contactName"
                      value={formData.contactName}
                      onChange={(e) => setFormData(prev => ({ ...prev, contactName: e.target.value }))}
                      placeholder="John Smith"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contactEmail">
                      <Mail className="inline h-4 w-4 mr-1" />
                      Contact Email
                    </Label>
                    <Input
                      id="contactEmail"
                      type="email"
                      value={formData.contactEmail}
                      onChange={(e) => setFormData(prev => ({ ...prev, contactEmail: e.target.value }))}
                      placeholder="john@example.com"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contactPhone">
                      <Phone className="inline h-4 w-4 mr-1" />
                      Contact Phone
                    </Label>
                    <Input
                      id="contactPhone"
                      value={formData.contactPhone}
                      onChange={(e) => setFormData(prev => ({ ...prev, contactPhone: e.target.value }))}
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>
              )}
            </div>

            <div>
              <Label htmlFor="notes">Internal Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                rows={3}
                placeholder="Any additional notes about this event..."
              />
            </div>
          </div>

          <DialogFooter className="flex-col-reverse sm:flex-row gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="w-full sm:w-auto">
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={mutation.isPending}
              className="w-full sm:w-auto"
            >
              {mutation.isPending ? "Saving..." : event ? "Update Event" : "Create Event"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Venue Form Modal */}
      <VenueFormModal
        open={showVenueModal}
        onOpenChange={setShowVenueModal}
        organisationId={organisationId}
        onVenueCreated={handleVenueCreated}
      />
    </>
  );
}